import { Button, DatePicker, Modal, Radio, Spin, message } from "antd";
import axios from "../../../context/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import { useState } from "react";
import { usePaginationPrzekazy } from "../../../context/PaginationContextPrzekazy";
import UserCan from "../../../components/UserCan";
import dayjs from "dayjs";
import datetimeParser from "../../../components/datetimeParser";

const GenerateXML = ({ ids, selectedRows, setSelectedRowKeys }) => {
	const user = useAuth();
	const { pagination, setPagination } = usePaginationPrzekazy();
	const [isLoading, setIsLoading] = useState(false);
	const [isStepModalOpen, setIsStepModalOpen] = useState(false);
	const [step, setStep] = useState(1);
	const [shouldSetDate, setShouldSetDate] = useState(false);
	const [selectedDate, setSelectedDate] = useState(null);
	const currentForwardDates = Array.from(
		new Set(
			(selectedRows || [])
				.map((row) => String(row?.msr_wplywy_date_forward || ""))
				.filter((value) => value && value !== "null"),
		),
	);
	const currentForwardDateLabel =
		currentForwardDates.length === 0
			? "brak ustawionej daty"
			: currentForwardDates.length === 1
				? datetimeParser(currentForwardDates[0], "YYYY-MM-DD")
				: "różne daty";

	const generateXML = async () => {
		setIsLoading(true);
		try {
			const response = await axios.post("/przekazania/generate/xml", {
				ids: ids,
				user: user.user.user.mlg_users_id,
				setDateForward: shouldSetDate,
				dateForward: shouldSetDate
					? Math.floor(
							new Date(
								selectedDate.format("YYYY-MM-DD"),
							).getTime() / 1000,
						)
					: null,
			});

			const fileobject = await axios.post(
				"/przekazania/download/xml",
				{ filename: response.data.data },
				{ responseType: "blob" },
			);

			const blob = new Blob([fileobject.data], {
				type: "application/xml",
			});
			const link = document.createElement("a");
			link.href = window.URL.createObjectURL(blob);
			link.download = `${response.data.data}`;
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (error) {
			const serverMessage =
				error?.response?.data?.message ||
				"Nie udało się wygenerować pliku XML przekazów";
			message.error(serverMessage);
			console.error("Błąd pobierania pliku:", error);
		} finally {
			setTimeout(() => {
				setIsLoading(false);
				setIsStepModalOpen(false);
				setStep(1);
				setShouldSetDate(false);
				setSelectedDate(null);
				setSelectedRowKeys([]);
				setPagination({ ...pagination, reload: !pagination.reload });
			}, 1000);
		}
	};

	const openModal = () => {
		setIsStepModalOpen(true);
		setStep(1);
	};

	const closeModal = () => {
		if (isLoading) {
			return;
		}
		setIsStepModalOpen(false);
		setStep(1);
		setShouldSetDate(false);
		setSelectedDate(null);
	};

	const goToConfirmStep = () => {
		if (shouldSetDate && !selectedDate) {
			message.warning(
				"Wybierz datę albo zaznacz opcję bez ustawiania daty.",
			);
			return;
		}
		setStep(2);
	};

	return (
		<>
			<UserCan perms={["can_generate_xml"]}>
				<Button
					type="primary"
					onClick={openModal}
					disabled={!ids.length > 0}
				>
					Wygeneruj przekazy
				</Button>
			</UserCan>

			<Modal
				open={isStepModalOpen}
				title={
					step === 1
						? "Generowanie XML - krok 1/2"
						: "Generowanie XML - krok 2/2"
				}
				onCancel={closeModal}
				footer={
					step === 1
						? [
								<Button
									key="cancel"
									onClick={closeModal}
								>
									Anuluj
								</Button>,
								<Button
									key="next"
									type="primary"
									onClick={goToConfirmStep}
								>
									Dalej
								</Button>,
							]
						: [
								<Button
									key="back"
									onClick={() => setStep(1)}
									disabled={isLoading}
								>
									Wstecz
								</Button>,
								<Button
									key="confirm"
									type="primary"
									loading={isLoading}
									onClick={generateXML}
								>
									Potwierdź i wygeneruj
								</Button>,
							]
				}
			>
				{step === 1 ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 12,
						}}
					>
						<div>
							Czy chcesz zmienić datę wpływu na rachunek
							rozliczeniowy ({currentForwardDateLabel})?
						</div>
						<Radio.Group
							value={shouldSetDate ? "yes" : "no"}
							onChange={(e) => {
								const next = e.target.value === "yes";
								setShouldSetDate(next);
								if (!next) {
									setSelectedDate(null);
								}
							}}
						>
							<Radio value="no">NIE</Radio>
							<Radio value="yes">TAK</Radio>
						</Radio.Group>
						{shouldSetDate && (
							<DatePicker
								style={{ width: "100%" }}
								placeholder="Wybierz datę"
								value={selectedDate}
								onChange={(value) => setSelectedDate(value)}
								allowClear={false}
								disabledDate={(current) =>
									current &&
									current >
										dayjs().add(7, "days").endOf("day")
								}
							/>
						)}
					</div>
				) : (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 8,
						}}
					>
						<div>
							Czy na pewno chcesz wygenerować XML dla {ids.length}{" "}
							zaznaczonych przekazów?
						</div>
						<div>
							Data wpływu na rachunek rozliczeniowy:{" "}
							{shouldSetDate && selectedDate
								? datetimeParser(
										Math.floor(
											new Date(
												selectedDate.format(
													"YYYY-MM-DD",
												),
											).getTime() / 1000,
										),
										"YYYY-MM-DD",
									)
								: "nie ustawiaj"}
						</div>
					</div>
				)}
			</Modal>

			<Modal
				open={isLoading}
				footer={null}
				closable={false}
				centered
			>
				<div style={{ textAlign: "center", padding: "20px 0" }}>
					<Spin size="large" />
					<p style={{ marginTop: 20 }}>
						Generowanie pliku XML, proszę czekać...
					</p>
				</div>
			</Modal>
		</>
	);
};

export default GenerateXML;
